import { useState, useEffect } from 'react';
import { X, Loader2, Download } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import * as Sentry from '@sentry/react';

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoPath: string;
  reviewerName: string;
  experimentName: string;
}

const VideoModal = ({ isOpen, onClose, videoPath, reviewerName, experimentName }: VideoModalProps) => {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getVideoUrl = async (videoPath: string): Promise<string> => {
    try {
      const { data, error } = await supabase.functions.invoke('tastermonial-api', {
        body: {
          method: 'GET',
          endpoint: '/api/v2/storage/url',
          queryString: `path=${encodeURIComponent(`s3://tastermonialuservideos/${videoPath}`)}`
        }
      });

      if (error) {
        throw new Error(`Failed to get video URL: ${error.message}`);
      }

      // The API returns { url: string, expires_in: number }
      if (!data.data || !data.data.url) {
        throw new Error('Invalid response: missing URL');
      }

      return data.data.url;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  };

  useEffect(() => {
    if (isOpen && videoPath) {
      setLoading(true);
      setError(null);
      setVideoUrl(null);

      getVideoUrl(videoPath)
        .then(url => {
          setVideoUrl(url);
        })
        .catch(err => {
          console.error('Failed to get video URL:', err);
          setError('Failed to load video');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isOpen, videoPath]);

  const handleClose = () => {
    setVideoUrl(null);
    setError(null);
    onClose();
  };

  const handleDownload = () => {
    if (videoUrl) {
      const link = document.createElement('a');
      link.href = videoUrl;
      link.download = `video-${Date.now()}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-lg w-full max-w-sm max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-lg font-semibold">{reviewerName}</h3>
            <p className="text-sm text-gray-600">{experimentName}</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 flex-1 flex flex-col">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-gray-600" />
                <p className="text-gray-600">Loading video...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-red-600 mb-4">{error}</p>
                <button
                  onClick={() => {
                    setError(null);
                    setLoading(true);
                    getVideoUrl(videoPath)
                      .then(url => setVideoUrl(url))
                      .catch(err => {
                        console.error('Failed to get video URL:', err);
                        setError('Failed to load video');
                      })
                      .finally(() => setLoading(false));
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : videoUrl ? (
            <div className="flex flex-col h-full">
              <div className="flex-1 aspect-[3/4] w-full mb-4">
                <video
                  src={videoUrl}
                  controls
                  className="w-full h-full object-contain"
                  autoPlay
                >
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="mt-auto">
                <button
                  onClick={handleDownload}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-glyco-purple text-white rounded-lg hover:bg-glyco-purple/90 transition-colors font-medium"
                >
                  <Download className="w-4 h-4" />
                  Download Video
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default VideoModal;
